import { AfterViewInit, Component, effect, ElementRef, input, OnDestroy, output, viewChild } from '@angular/core';
import { IVideoPlayer } from '../../embedded-video-player.interface';

interface IYoutubePlayer {
  addEventListener(event: string, callback: (event: IYoutubePlayerStateChangeEvent) => void): void;
  getCurrentTime(): number;
  getDuration(): number;
  play(): void;
  pause(): void;
  seekTo(time: number, allowSeekAhead?: boolean): void;
  setVolume(volume: number): void;
  setMuted(muted: boolean): void;
}

interface IYoutubePlayerReadyEvent {
  target: IYoutubePlayer;
}

interface IYoutubePlayerStateChangeEvent {
  target: IYoutubePlayer;
  data: number;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  interface Window {
    YT?: {
      Player: new (
        elementId: string,
        options: {
          videoId: string;
          width?: string | number;
          height?: string | number;
          playerVars?: {
            autoplay?: number;
            mute?: number;
            controls?: number;
          };
        },
      ) => IYoutubePlayer;
      PlayerState: {
        PLAYING: 1;
        PAUSED: 2;
        ENDED: 0;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

@Component({
  imports: [],
  selector: 'app-youtube-player',
  styleUrl: './youtube-player.component.scss',
  templateUrl: './youtube-player.component.html',
})
export class YoutubePlayerComponent implements AfterViewInit, OnDestroy, IVideoPlayer {
  public readonly videoUrl = input.required<string>();
  public readonly autoPlay = input<boolean>(false);
  public readonly showControls = input<boolean>(true);

  public readonly playerStateChange = output<{
    isPlaying: boolean;
    currentTime: number;
    duration: number;
  }>();

  public readonly playerReady = output<boolean>();

  public readonly youtubePlayerContainer = viewChild<ElementRef>('youtubePlayerContainer');

  public readonly playerId = `youtube-player-${Math.random().toString(36).substr(2, 9)}`;

  private player: IYoutubePlayer | null = null;

  private currentTime = 0;
  private duration = 0;
  private playing = false;
  private volumeLevel = 1;
  private mutedState = false;

  private stateUpdateInterval: number | null = null;
  private initialPauseApplied = false;

  constructor() {
    effect(() => {
      if (this.videoUrl()) {
        this.initializePlayer();
      }
    });
  }

  public ngAfterViewInit(): void {
    this.initializePlayer();
  }

  public ngOnDestroy(): void {
    if (this.stateUpdateInterval) {
      window.clearInterval(this.stateUpdateInterval);
    }

    if (this.player) {
      try {
        this.player.pause();
      } catch {}
      this.player = null;
    }

    this.removeYoutubeScript();
  }

  public play(): void {
    if (this.player) {
      this.player.play();
      this.playing = true;
      this.emitStateChange();
    }
  }

  public pause(): void {
    if (this.player) {
      this.player.pause();
      this.playing = false;
      this.emitStateChange();
    }
  }

  public getDuration(): number {
    return this.duration;
  }

  public getCurrentTime(): number {
    return this.currentTime;
  }

  public isPlaying(): boolean {
    return this.playing;
  }

  public setVolume(volume: number): void {
    if (this.player) {
      this.player.setVolume(volume);
      this.volumeLevel = volume;

      if (volume === 0) {
        this.mutedState = true;
      } else if (this.mutedState) {
        this.mutedState = false;
      }
    }
  }

  public getVolume(): number {
    return this.volumeLevel;
  }

  public mute(): void {
    if (this.player) {
      this.player.setMuted(true);
      this.mutedState = true;
    }
  }

  public unmute(): void {
    if (this.player) {
      this.player.setMuted(false);
      this.mutedState = false;
    }
  }

  public isMuted(): boolean {
    return this.mutedState;
  }

  private initializePlayer(): void {
    this.removeYoutubeScript();

    this.loadYoutubeScript().then(() => {
      this.initPlayer();
      this.playerReady.emit(true);
    });
  }

  private loadYoutubeScript(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve();

        return;
      }

      if (window.onYouTubeIframeAPIReady) {
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = (): void => {
          prev();
          resolve();
        };

        return;
      }

      window.onYouTubeIframeAPIReady = (): void => {
        resolve();
      };

      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.async = true;
      document.body.appendChild(script);
    });
  }

  public seek(time: number): void {
    if (this.player) {
      this.player.seekTo(time, true);
      this.currentTime = time;
      this.emitStateChange();
    }
  }
  private initPlayer(): void {
    if (!window.YT) {
      return;
    }

    const videoId = this.extractYoutubeVideoId(this.videoUrl());

    if (!videoId) {
      return;
    }

    const options = {
      events: {
        onReady: (event: IYoutubePlayerReadyEvent): void => {
          this.player = event.target;
          this.stateUpdateInterval = window.setInterval(() => {
            if (this.player) {
              this.currentTime = this.player.getCurrentTime() || 0;
              this.duration = this.player.getDuration() || 0;
              this.emitStateChange();
            }
          }, 1000);
        },
        onStateChange: (event: IYoutubePlayerStateChangeEvent): void => {
          const state = event.data;

          if (state === window.YT!.PlayerState.PLAYING) {
            this.playing = true;
            this.emitStateChange();
          } else if (state === window.YT!.PlayerState.PAUSED) {
            this.playing = false;
            this.emitStateChange();
          } else if (state === window.YT!.PlayerState.ENDED) {
            this.playing = false;
            this.emitStateChange();
          }
        },
      },
      height: '100%',
      playerVars: {
        autoplay: this.autoPlay() ? 1 : 0,
        controls: this.showControls() ? 1 : 0,
        mute: 0,
      },
      videoId,
      width: '100%',
    };

    new window.YT.Player(this.playerId, options);
  }

  private extractYoutubeVideoId(url: string): string | null {
    try {
      const parsedUrl = new URL(url);
      const host = parsedUrl.hostname;

      if (host.includes('youtu.be')) {
        const [shortPath] = parsedUrl.pathname.split('/').filter(Boolean);

        if (shortPath) {
          return shortPath;
        }
      }

      if (host.includes('youtube.com')) {
        const watchId = parsedUrl.searchParams.get('v');

        if (watchId) {
          return watchId;
        }

        const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
        const [, potentialId] = pathSegments;

        if (pathSegments.length >= 2 && potentialId) {
          const [firstSegment] = pathSegments;

          if (['shorts', 'live', 'embed'].includes(firstSegment)) {
            return potentialId;
          }
        }
      }
    } catch {
      // The URL constructor can throw for protocol-relative or invalid URLs. Fall back to regex parsing below.
    }

    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);

    if (watchMatch) {
      return watchMatch[1];
    }

    const pathMatch = url.match(/(?:shorts|live|embed)\/([a-zA-Z0-9_-]+)/);

    if (pathMatch) {
      return pathMatch[1];
    }

    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);

    if (shortMatch) {
      return shortMatch[1];
    }

    return null;
  }

  private emitStateChange(): void {
    this.playerStateChange.emit({
      currentTime: this.currentTime,
      duration: this.duration,
      isPlaying: this.playing,
    });
  }

  private removeYoutubeScript(): void {
    const youtubeScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');

    if (youtubeScript) {
      youtubeScript.remove();
    }
  }
}
